import { TICK_RATE } from './constants.ts';
import type { Vec2 } from './math.ts';
import type { HeroId, MinionKind, StructureKind, Team } from './types.ts';

/**
 * Tres líneas entre los nexos (azul en -Z, rojo en +Z): la central es recta y
 * las laterales rodean el mapa por los lados. Cada recorrido va del nexo azul al
 * rojo; los minions rojos lo recorren al revés.
 *
 * Protección: en cada línea la torre interior (tier 2) sólo recibe daño cuando
 * cae la exterior (tier 1). Las dos torres del nexo (tier 3, sin línea) se pueden
 * atacar cuando el enemigo abre por completo cualquier línea, y el nexo (tier 4)
 * sólo cuando caen esas dos torres.
 */
export const LANE_HALF_WIDTH = 3.2;
export const NEXUS_Z = 54;
export const LANE_COUNT = 3;
export const LANE_NAMES = ['izquierda', 'centro', 'derecha'];

export const LANE_PATHS: Vec2[][] = [
  [{ x: 0, z: -NEXUS_Z }, { x: -42, z: -33 }, { x: -42, z: 33 }, { x: 0, z: NEXUS_Z }],
  [{ x: 0, z: -NEXUS_Z }, { x: 0, z: NEXUS_Z }],
  [{ x: 0, z: -NEXUS_Z }, { x: 42, z: -33 }, { x: 42, z: 33 }, { x: 0, z: NEXUS_Z }],
];

/** Recorrido de una línea en el sentido de avance de `team`. */
export function laneWaypoints(lane: number, team: Team): Vec2[] {
  const path = LANE_PATHS[lane];
  return team === 'blue' ? path : [...path].reverse();
}

export function pathLength(path: Vec2[]): number {
  let total = 0;
  for (let i = 1; i < path.length; i++) total += Math.hypot(path[i].x - path[i - 1].x, path[i].z - path[i - 1].z);
  return total;
}

/** Punto a `distance` del inicio del recorrido, con la dirección de avance. */
export function pointAlong(path: Vec2[], distance: number): { x: number; z: number; dirX: number; dirZ: number } {
  let left = distance;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const dirX = (b.x - a.x) / len;
    const dirZ = (b.z - a.z) / len;
    if (left <= len || i === path.length - 1) {
      const t = Math.min(left, len);
      return { x: a.x + dirX * t, z: a.z + dirZ * t, dirX, dirZ };
    }
    left -= len;
  }
  return { x: path[0].x, z: path[0].z, dirX: 0, dirZ: 1 };
}

export interface StructureSpec {
  kind: StructureKind;
  team: Team;
  tier: number;
  /** línea (0-2); -1 para el nexo y sus torres (tier 3) */
  lane: number;
  model: HeroId;
  x: number;
  z: number;
}

/** fracción del recorrido (desde el propio nexo) donde va cada torre */
const TOWER_FRACTIONS: Record<number, number> = { 2: 0.15, 1: 0.32 };
/** torres del nexo: sobre la bisectriz entre la línea central y las laterales */
const NEXUS_TOWER_OFFSET = { x: 4.7, z: 7.7 };
/** separación lateral de la torre respecto al centro de la línea */
const TOWER_SIDE_OFFSET = 2.3;
const TOWER_MODELS: Record<Team, HeroId[]> = {
  blue: ['mage', 'falco', 'knight'],
  red: ['mario', 'sheik', 'barbarian'],
};

function buildLayout(): StructureSpec[] {
  const specs: StructureSpec[] = [];
  for (const team of ['blue', 'red'] as Team[]) {
    const side = team === 'blue' ? -1 : 1;
    specs.push({
      kind: 'nexus', team, tier: 4, lane: -1,
      model: team === 'blue' ? 'knight' : 'barbarian',
      x: 0, z: side * NEXUS_Z,
    });
    // dos torres del nexo (tier 3) entre la línea central y las laterales,
    // lo bastante separadas para no cortar el paso de los minions
    for (const [i, sx] of [-1, 1].entries()) {
      specs.push({
        kind: 'tower', team, tier: 3, lane: -1,
        model: TOWER_MODELS[team][i === 0 ? 2 : 0],
        x: sx * NEXUS_TOWER_OFFSET.x,
        z: side * (NEXUS_Z - NEXUS_TOWER_OFFSET.z),
      });
    }
    for (let lane = 0; lane < LANE_COUNT; lane++) {
      const path = laneWaypoints(lane, team);
      const length = pathLength(path);
      for (const tier of [1, 2]) {
        const p = pointAlong(path, length * TOWER_FRACTIONS[tier]);
        // al costado del sentido de avance: nunca corta el paso de los minions
        specs.push({
          kind: 'tower', team, tier, lane,
          model: TOWER_MODELS[team][(lane + tier) % 3],
          x: p.x - p.dirZ * TOWER_SIDE_OFFSET,
          z: p.z + p.dirX * TOWER_SIDE_OFFSET,
        });
      }
    }
  }
  return specs;
}

export const STRUCTURE_LAYOUT: StructureSpec[] = buildLayout();

export interface StructureAttack {
  range: number;
  cooldownTicks: number;
  damageVsHero: number;
  damageVsMinion: number;
}

export const STRUCTURE_STATS: Record<StructureKind, { maxHp: number; radius: number; attack: StructureAttack }> = {
  tower: {
    maxHp: 1400,
    radius: 1.1,
    attack: { range: 7.5, cooldownTicks: Math.round(1.1 * TICK_RATE), damageVsHero: 70, damageVsMinion: 55 },
  },
  nexus: {
    maxHp: 2600,
    radius: 2.0,
    attack: { range: 8.5, cooldownTicks: Math.round(0.9 * TICK_RATE), damageVsHero: 95, damageVsMinion: 70 },
  },
};

/** Proyectil de torre/nexo (compartido). */
export const TOWER = {
  /** alcance de una torre (anillo de rango del cliente y tests) */
  range: STRUCTURE_STATS.tower.attack.range,
  projectileSpeed: 20,
  projectileRadius: 0.4,
  projectileTtl: 3 * TICK_RATE,
};

export const MINION_STATS: Record<MinionKind, {
  maxHp: number;
  radius: number;
  speed: number;
  damage: number;
  /** daño contra estructuras (los minions empujan la línea) */
  structureDamage: number;
  range: number;
  aggroRange: number;
  cooldownTicks: number;
}> = {
  koopa: {
    maxHp: 160,
    radius: 0.42,
    speed: 3.4,
    damage: 15,
    structureDamage: 16.5,
    range: 1.3,
    aggroRange: 5.5,
    cooldownTicks: 30,
  },
};

export const MINION_WAVE = {
  firstAtTicks: 5 * TICK_RATE,
  everyTicks: 30 * TICK_RATE,
  /** minions por línea y equipo */
  size: 4,
  spacing: 1.1,
  /** distancia desde el nexo (a lo largo de la línea) donde aparecen */
  spawnDistance: 4.5,
};

export const GOLD = {
  /** al dar el golpe final a un minion */
  minion: 20,
  /** minion enemigo que muere sin que un héroe lo remate: cada héroe enemigo cerca (XP.range) se lleva esta fracción */
  minionMissedShare: 1 / 3,
  /** al matar a un héroe */
  heroKill: 300,
  /** a cada héroe aliado que participó en la muerte (sin ser el rematador) */
  assist: 150,
  /** reparto entre los aliados bajo la torre cuando cae */
  tower: 250,
  /** oro pasivo: cada héroe (vivo o muerto) gana esto por segundo durante la partida */
  passivePerSecond: 2,
};

/** Reaparición: 3 s la primera muerte, +2 s por cada muerte siguiente, máximo 30 s. */
/**
 * Reaparición según el avance de la partida: 3 s al empezar, sube de forma
 * lineal con el reloj y llega a 30 s a los 20 minutos (duración de la partida).
 */
export const RESPAWN = {
  baseTicks: 3 * TICK_RATE,
  maxTicks: 30 * TICK_RATE,
  rampTicks: 20 * 60 * TICK_RATE,
};

export function respawnTicksFor(elapsedTicks: number): number {
  const t = Math.max(0, Math.min(1, elapsedTicks / RESPAWN.rampTicks));
  return Math.round(RESPAWN.baseTicks + (RESPAWN.maxTicks - RESPAWN.baseTicks) * t);
}

/** Puntos de aparición de héroes junto al propio nexo. */
export function heroSpawn(team: Team, slot: number): { x: number; z: number } {
  const side = team === 'blue' ? -1 : 1;
  const offsets = [
    { x: 0, z: 4.5 },
    { x: -4, z: 3 },
    { x: 4, z: 3 },
  ];
  const o = offsets[slot % offsets.length];
  return { x: o.x, z: side * (NEXUS_Z - o.z) };
}
